import type { FeeSchedule } from "@netkeep/fee-types";

/**
 * Initial fee schedules.
 *
 * IMPORTANT — data honesty rules (see AGENTS.md):
 *  - Every value below was transcribed from the official page in `source.url`
 *    on TRANSCRIBED_ON. None has been reviewed by a human yet, so all are
 *    `verificationStatus: "UNVERIFIED"` with `lastVerifiedAt: null`.
 *  - Only an admin review (POST /api/v1/admin/fee-schedules/:id/verify) may
 *    mark a schedule VERIFIED.
 *  - Where a source page publishes no effective date, `effectiveFrom` is the
 *    transcription date and `notes` says so.
 *
 * IDs are fixed so the database seed and the mobile app's bundled fallback
 * refer to the same schedules.
 */
export const TRANSCRIBED_ON = "2026-10-15";

export type PlatformKind = "MARKETPLACE" | "PAYMENT_PROVIDER" | "OFFLINE";

export interface PlatformSeed {
  slug: string;
  name: string;
  kind: PlatformKind;
  websiteUrl: string | null;
}

export interface PaymentMethodSeed {
  slug: string;
  name: string;
  /** Platform that operates the payment rail, if any. */
  providerSlug: string | null;
}

export const PLATFORMS: PlatformSeed[] = [
  { slug: "ebay", name: "eBay", kind: "MARKETPLACE", websiteUrl: "https://www.ebay.com" },
  {
    slug: "facebook-marketplace",
    name: "Facebook Marketplace",
    kind: "MARKETPLACE",
    websiteUrl: "https://www.facebook.com/marketplace",
  },
  { slug: "paypal", name: "PayPal", kind: "PAYMENT_PROVIDER", websiteUrl: "https://www.paypal.com" },
  { slug: "mercari", name: "Mercari", kind: "MARKETPLACE", websiteUrl: "https://www.mercari.com" },
  { slug: "etsy", name: "Etsy", kind: "MARKETPLACE", websiteUrl: "https://www.etsy.com" },
];

export const PAYMENT_METHODS: PaymentMethodSeed[] = [
  { slug: "ebay-managed-payments", name: "eBay checkout", providerSlug: "ebay" },
  { slug: "facebook-checkout", name: "Facebook checkout", providerSlug: "facebook-marketplace" },
  { slug: "paypal-checkout", name: "PayPal Checkout / Invoice", providerSlug: "paypal" },
  { slug: "paypal-goods-services", name: "PayPal Goods & Services", providerSlug: "paypal" },
  { slug: "mercari-checkout", name: "Mercari checkout", providerSlug: "mercari" },
  { slug: "etsy-payments", name: "Etsy Payments", providerSlug: "etsy" },
];

const SOURCES = {
  ebay: {
    url: "https://www.ebay.com/help/selling/fees-credits-invoices/selling-fees?id=4822",
    title: "eBay Help — Selling fees",
  },
  meta: {
    url: "https://www.facebook.com/legal/merchant_policies",
    title: "Meta — Facebook Marketplace Seller Protection Policy (Selling Fee)",
  },
  paypal: {
    url: "https://www.paypal.com/us/business/paypal-business-fees",
    title: "PayPal — Merchant fees (US)",
  },
  mercari: {
    url: "https://www.mercari.com/us/help_center/article/169/",
    title: "Mercari Help Center — Fees on Mercari",
  },
  etsy: {
    url: "https://www.etsy.com/legal/fees/",
    title: "Etsy — Fees & Payments Policy",
  },
} as const;

export const PRESET_SCHEDULES: FeeSchedule[] = [
  {
    id: "0b0f1d0e-6a1f-4c1e-9d55-6f2f4f0a0001",
    name: "eBay — US, most categories, no Store",
    platform: { slug: "ebay", name: "eBay" },
    paymentMethod: { slug: "ebay-managed-payments", name: "eBay checkout" },
    region: "US",
    currency: "USD",
    transactionType: "SHIPPED",
    sellerTier: "no-store",
    itemCategory: "most-categories",
    version: 1,
    versionLabel: "transcribed-2026-10-15",
    status: "ACTIVE",
    verificationStatus: "UNVERIFIED",
    effectiveFrom: TRANSCRIBED_ON,
    effectiveTo: null,
    lastVerifiedAt: null,
    source: SOURCES.ebay,
    notes:
      "Source page publishes no effective date. Final value fee is charged on the total amount of the sale (item, handling, shipping charged, sales tax, other applicable fees); exceptions exist for some shipping options and categories. Payment processing is included. Promoted listing (general) fees are charged on the total sale amount including shipping and taxes. Store subscriber and category-specific rates are not modeled yet.",
    taxCollectedBy: "MARKETPLACE",
    paymentProcessingIncluded: true,
    promotedListingBasis: ["ITEM_PRICE", "BUYER_SHIPPING", "SALES_TAX"],
    rules: [
      {
        code: "FINAL_VALUE",
        label: "Final value fee",
        category: "MARKETPLACE",
        basis: ["ITEM_PRICE", "BUYER_SHIPPING", "SALES_TAX"],
        percentTiers: [
          { upTo: "7500", percent: "13.6" },
          { upTo: null, percent: "2.35" },
        ],
        fixedTiers: [
          { upTo: "10", amount: "0.30" },
          { upTo: null, amount: "0.40" },
        ],
      },
    ],
  },
  {
    id: "0b0f1d0e-6a1f-4c1e-9d55-6f2f4f0a0002",
    name: "Facebook Marketplace — US, shipped via Facebook checkout",
    platform: { slug: "facebook-marketplace", name: "Facebook Marketplace" },
    paymentMethod: { slug: "facebook-checkout", name: "Facebook checkout" },
    region: "US",
    currency: "USD",
    transactionType: "SHIPPED",
    sellerTier: "individual",
    itemCategory: "all",
    version: 1,
    versionLabel: "transcribed-2026-10-15",
    status: "ACTIVE",
    verificationStatus: "UNVERIFIED",
    effectiveFrom: TRANSCRIBED_ON,
    effectiveTo: null,
    lastVerifiedAt: null,
    source: SOURCES.meta,
    notes:
      "Policy page states 10% per transaction, minimum $0.80, on the entire transaction amount including shipping and applicable taxes. The page has no effective date, and Meta has separately published changes to Shops/checkout — confirm availability of Marketplace checkout before verifying.",
    taxCollectedBy: "MARKETPLACE",
    paymentProcessingIncluded: true,
    promotedListingBasis: ["ITEM_PRICE"],
    rules: [
      {
        code: "SELLING_FEE",
        label: "Selling fee",
        category: "MARKETPLACE",
        basis: ["ITEM_PRICE", "BUYER_SHIPPING", "SALES_TAX"],
        percent: "10",
        minimumFee: "0.80",
      },
    ],
  },
  {
    id: "0b0f1d0e-6a1f-4c1e-9d55-6f2f4f0a0005",
    name: "PayPal — invoice paid with PayPal Checkout",
    platform: { slug: "paypal", name: "PayPal" },
    paymentMethod: { slug: "paypal-checkout", name: "PayPal Checkout / Invoice" },
    region: "US",
    currency: "USD",
    transactionType: "INVOICE",
    sellerTier: "business",
    itemCategory: "all",
    version: 1,
    versionLabel: "transcribed-2026-10-15",
    status: "ACTIVE",
    verificationStatus: "UNVERIFIED",
    effectiveFrom: TRANSCRIBED_ON,
    effectiveTo: null,
    lastVerifiedAt: null,
    source: SOURCES.paypal,
    notes:
      "Source page 'Last Updated: October 1, 2026'. Domestic invoice paid via PayPal Checkout/Venmo/Guest Checkout: 3.49% + USD 0.49 fixed fee. Card-funded invoice payments (2.99% + fixed) are not modeled as a separate schedule yet. Basis assumed to be the full invoice total.",
    taxCollectedBy: "SELLER",
    paymentProcessingIncluded: false,
    promotedListingBasis: ["ITEM_PRICE"],
    rules: [
      {
        code: "PAYPAL_CHECKOUT",
        label: "PayPal Checkout fee",
        category: "PAYMENT_PROCESSING",
        basis: ["ITEM_PRICE", "BUYER_SHIPPING", "SALES_TAX"],
        percent: "3.49",
        fixedAmount: "0.49",
      },
    ],
  },
  {
    id: "0b0f1d0e-6a1f-4c1e-9d55-6f2f4f0a0008",
    name: "PayPal — Goods & Services payment",
    platform: { slug: "paypal", name: "PayPal" },
    paymentMethod: { slug: "paypal-goods-services", name: "PayPal Goods & Services" },
    region: "US",
    currency: "USD",
    transactionType: "SHIPPED",
    sellerTier: "individual",
    itemCategory: "all",
    version: 1,
    versionLabel: "transcribed-2026-10-15",
    status: "ACTIVE",
    verificationStatus: "UNVERIFIED",
    effectiveFrom: TRANSCRIBED_ON,
    effectiveTo: null,
    lastVerifiedAt: null,
    source: SOURCES.paypal,
    notes:
      "Source page 'Last Updated: October 1, 2026'. Buyer pays directly with PayPal 'Send/Receive Money for Goods and Services' (2.99%, no fixed fee in the US table), for sales arranged anywhere (Facebook, Instagram, forums). No marketplace fee is included; if the sale also went through a marketplace checkout, use that marketplace's profile instead. The fee is assumed to apply to the full amount the buyer sends (item + shipping + any tax); the source does not state the basis explicitly.",
    taxCollectedBy: "NONE",
    paymentProcessingIncluded: false,
    promotedListingBasis: ["ITEM_PRICE"],
    rules: [
      {
        code: "PAYPAL_GOODS_SERVICES",
        label: "PayPal Goods & Services fee",
        category: "PAYMENT_PROCESSING",
        basis: ["ITEM_PRICE", "BUYER_SHIPPING", "SALES_TAX"],
        percent: "2.99",
      },
    ],
  },
  {
    id: "0b0f1d0e-6a1f-4c1e-9d55-6f2f4f0a0006",
    name: "Mercari — US, shipped via Mercari checkout",
    platform: { slug: "mercari", name: "Mercari" },
    paymentMethod: { slug: "mercari-checkout", name: "Mercari checkout" },
    region: "US",
    currency: "USD",
    transactionType: "SHIPPED",
    sellerTier: "individual",
    itemCategory: "all",
    version: 1,
    versionLabel: "transcribed-2026-10-15",
    status: "ACTIVE",
    verificationStatus: "UNVERIFIED",
    effectiveFrom: TRANSCRIBED_ON,
    effectiveTo: null,
    lastVerifiedAt: null,
    source: SOURCES.mercari,
    notes:
      "Source page 'Updated 05/07/2026'; fee structure 'Effective January 6, 2025'. 10% selling fee on item price + buyer-paid shipping for new and updated listings; no separate payment processing fee for the seller (the 3.6% Buyer Protection fee is paid by the buyer and not modeled). Listings not updated since the change may still carry the old structure. Optional seller costs (Instant Pay $3 per cash-out, cancelation fee, Mercari Authenticate) are not modeled. The page does not say who collects sales tax; marketplace collection is assumed.",
    taxCollectedBy: "MARKETPLACE",
    paymentProcessingIncluded: true,
    promotedListingBasis: ["ITEM_PRICE"],
    rules: [
      {
        code: "SELLING_FEE",
        label: "Selling fee",
        category: "MARKETPLACE",
        basis: ["ITEM_PRICE", "BUYER_SHIPPING"],
        percent: "10",
      },
    ],
  },
  {
    id: "0b0f1d0e-6a1f-4c1e-9d55-6f2f4f0a0007",
    name: "Etsy — US seller, Etsy Payments",
    platform: { slug: "etsy", name: "Etsy" },
    paymentMethod: { slug: "etsy-payments", name: "Etsy Payments" },
    region: "US",
    currency: "USD",
    transactionType: "SHIPPED",
    sellerTier: "standard",
    itemCategory: "all",
    version: 1,
    versionLabel: "transcribed-2026-10-15",
    status: "ACTIVE",
    verificationStatus: "UNVERIFIED",
    effectiveFrom: TRANSCRIBED_ON,
    effectiveTo: null,
    lastVerifiedAt: null,
    source: SOURCES.etsy,
    notes:
      "Policy reported as 'last updated October 5, 2026'. Etsy blocked automated access to the page, so these values come from Etsy's published policy text as indexed by search and MUST be checked on the live page before verifying. Transaction fee 6.5% of displayed price + shipping + gift wrapping; for US sellers it does not apply to sales tax. Payment processing (US bank account) 3% + $0.25 on the gross order amount including shipping and tax. The $0.20 listing fee is modeled as one listing per sale. Offsite Ads, Etsy Ads, gift wrapping and currency conversion (2.5%) are not modeled.",
    taxCollectedBy: "MARKETPLACE",
    paymentProcessingIncluded: false,
    promotedListingBasis: ["ITEM_PRICE", "BUYER_SHIPPING"],
    rules: [
      {
        code: "TRANSACTION_FEE",
        label: "Transaction fee",
        category: "MARKETPLACE",
        basis: ["ITEM_PRICE", "BUYER_SHIPPING"],
        percent: "6.5",
      },
      {
        code: "PAYMENT_PROCESSING",
        label: "Etsy Payments processing fee",
        category: "PAYMENT_PROCESSING",
        basis: ["ITEM_PRICE", "BUYER_SHIPPING", "SALES_TAX"],
        percent: "3",
        fixedAmount: "0.25",
      },
      {
        code: "LISTING_FEE",
        label: "Listing fee",
        category: "OTHER",
        basis: ["ITEM_PRICE"],
        fixedAmount: "0.20",
      },
    ],
  },
];

export const PRESET_SOURCES = Object.values(SOURCES);
